import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { ChevronLeft, HelpCircle, PackageCheck, Search } from 'lucide-react'
import './Subscriptions.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import { drumtaleAdminSessionAtom } from '../../AtomStore'
import { gradeAccessPolicy, openPageSpecification } from '../shared/pageSpecification'
import {
  Payment,
  Product,
  Subscription,
  paymentIssues,
  products as catalogProducts,
  subscriptionsDataModelGuide,
  subscriptionsImplementationGuide,
  subscriptionsSpecification,
  subscriptionsStateDefinitions,
} from './subscriptionsData'

const TOAST_DURATION_MS = 2400

type SubscriptionTab = 'subsStatus' | 'payments' | 'products' | 'errors'
type PeriodMode = 'day' | 'week' | 'month' | 'year'
type PackageType = '드럼 전용' | '인지 전용' | '드럼+인지 통합 패키지'

interface ProductDraft {
  name: string
  amount: string
  packageType: PackageType
  id: string
  base: string
  offer: string
  sale: string
  drum: boolean
  cognitive: boolean
}

const TABS: { id: SubscriptionTab; label: string }[] = [
  { id: 'subsStatus', label: '스토어 구독현황' },
  { id: 'payments', label: '결제내역' },
  { id: 'products', label: '구독 상품 설정' },
  { id: 'errors', label: '결제 오류관리' },
]

function matchesPeriod(value: string, start: string, end: string) {
  if (!value || value === '-') return false
  const dateText = value.slice(0, 10)
  return dateText >= (start || '1900-01-01') && dateText <= (end || '2999-12-31')
}

function matchesTextSearch(query: string, ...values: string[]) {
  if (!query) return true
  return values.join(' ').toLowerCase().includes(query.toLowerCase())
}

function subscriptionStatusBadgeType(status: string) {
  return status === '구독 중' || status === '무료 체험 중' ? 'success' : 'warning'
}

function paymentStatusBadgeType(status: string) {
  return status === '완료' || status === '무료 체험' ? 'success' : 'danger'
}

function ackBadgeType(ack: string) {
  return ack === '승인 완료' ? 'success' : 'warning'
}

function packageTypeFromRights(rights: string): PackageType {
  if (rights === '드럼') return '드럼 전용'
  if (rights === '인지') return '인지 전용'
  return '드럼+인지 통합 패키지'
}

function draftFromProduct(product: Product): ProductDraft {
  return {
    name: product.name,
    amount: product.amount,
    packageType: packageTypeFromRights(product.rights),
    id: product.id,
    base: product.base,
    offer: product.offer,
    sale: product.sale === '중지' ? '중지' : '판매중',
    drum: product.rights.includes('드럼'),
    cognitive: product.rights.includes('인지'),
  }
}

function productFromDraft(draft: ProductDraft): Product {
  const rights = draft.drum && draft.cognitive ? '드럼+인지' : draft.drum ? '드럼' : draft.cognitive ? '인지' : '-'
  const grade = draft.drum && draft.cognitive ? '플러스' : draft.drum ? '드러머' : draft.cognitive ? '인지' : '일반'
  return {
    name: draft.name.trim() || '미정 상품',
    id: draft.id.trim() || 'unmapped.sku',
    base: draft.base.trim() || '-',
    offer: draft.offer.trim() || '-',
    grade,
    rights,
    sale: draft.sale,
    amount: draft.amount.trim() || '0',
  }
}

function Subscriptions() {
  const navigate = useNavigate()
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)

  const [activeTab, setActiveTab] = useState<SubscriptionTab>('subsStatus')
  const [search, setSearch] = useState('')

  const [subscriptionPeriodStart, setSubscriptionPeriodStart] = useState('2026-07-01')
  const [subscriptionPeriodEnd, setSubscriptionPeriodEnd] = useState('2026-07-28')

  const [paymentPeriodStart, setPaymentPeriodStart] = useState('2026-07-01')
  const [paymentPeriodEnd, setPaymentPeriodEnd] = useState('2026-07-28')

  const subscriptions: Subscription[] = []
  const payments: Payment[] = []
  const [productList, setProductList] = useState<Product[]>(() => catalogProducts.map((item) => ({ ...item })))
  const [editingProductId, setEditingProductId] = useState<string | null>(null)
  const [productDraft, setProductDraft] = useState<ProductDraft | null>(null)

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAdminSessionActive) {
      navigate('/')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const applyPeriodPreset = (setStart: (value: string) => void, setEnd: (value: string) => void, mode: PeriodMode) => {
    if (mode === 'year') {
      setStart('2025-07-28')
      setEnd('2026-07-28')
    } else if (mode === 'week') {
      setStart('2026-07-22')
      setEnd('2026-07-28')
    } else if (mode === 'day') {
      setStart('2026-07-28')
      setEnd('2026-07-28')
    } else {
      setStart('2026-07-01')
      setEnd('2026-07-28')
    }
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(subscriptionsSpecification, subscriptionsDataModelGuide, subscriptionsImplementationGuide, {
      gradeAccessPolicy,
      stateDefinitions: subscriptionsStateDefinitions,
    })
    if (!opened) {
      showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
    }
  }

  const openProductEditor = (product: Product) => {
    setEditingProductId(product.id)
    setProductDraft(draftFromProduct(product))
  }

  const closeProductEditor = () => {
    setEditingProductId(null)
    setProductDraft(null)
  }

  const updateProductDraft = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) => {
    setProductDraft((current) => (current ? { ...current, [key]: value } : current))
  }

  const handlePackageTypeChange = (packageType: PackageType) => {
    setProductDraft((current) => current ? {
      ...current,
      packageType,
      drum: packageType !== '인지 전용',
      cognitive: packageType !== '드럼 전용',
    } : current)
  }

  const handleRightToggle = (key: 'drum' | 'cognitive', checked: boolean) => {
    setProductDraft((current) => {
      if (!current) return current
      const next = { ...current, [key]: checked }
      if (next.drum && next.cognitive) next.packageType = '드럼+인지 통합 패키지'
      else if (next.drum) next.packageType = '드럼 전용'
      else if (next.cognitive) next.packageType = '인지 전용'
      return next
    })
  }

  const handleSaveProduct = () => {
    if (!editingProductId || !productDraft) return
    const saved = productFromDraft(productDraft)
    setProductList((current) => current.map((item) => (item.id === editingProductId ? saved : item)))
    closeProductEditor()
    showToast('저장되었습니다. 목록에 반영됩니다.')
  }

  const filteredSubscriptions = useMemo(() => {
    return subscriptions
      .filter((subscription) => matchesPeriod(subscription.paidAt, subscriptionPeriodStart, subscriptionPeriodEnd))
      .filter((subscription) => matchesTextSearch(search, subscription.member, subscription.product, subscription.store, subscription.status))
  }, [search, subscriptionPeriodStart, subscriptionPeriodEnd])

  const filteredPayments = useMemo(() => {
    return payments
      .filter((payment) => matchesPeriod(payment.paidAt, paymentPeriodStart, paymentPeriodEnd))
      .filter((payment) => matchesTextSearch(search, payment.member, payment.order, payment.product, payment.status))
  }, [search, paymentPeriodStart, paymentPeriodEnd])

  return (
    <div className='Subscriptions'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              {productDraft && (
                <button className='back-button' type='button' onClick={closeProductEditor}>
                  <ChevronLeft size={16} />
                  뒤로가기
                </button>
              )}
              <h1>{productDraft ? '구독 상품 설정' : '구독관리'}</h1>
            </div>
          </header>

          <section className='view active'>
            {productDraft ? (
              <div className='detail-stack'>
                <section className='detail-hero'>
                  <p className='eyebrow'>요금제 및 인앱 상품 SKU 매핑</p>
                  <h2>{productDraft.name}</h2>
                  <p>OS 인앱 상품 SKU와 백엔드 접근 권한을 연결합니다.</p>
                </section>

                <section className='panel'>
                  <div className='modal-grid'>
                    <label className='modal-field'>
                      요금제명
                      <input value={productDraft.name} onChange={(event) => updateProductDraft('name', event.target.value)} />
                    </label>
                    <label className='modal-field'>
                      월 구독 금액(KRW)
                      <input value={productDraft.amount} onChange={(event) => updateProductDraft('amount', event.target.value)} />
                    </label>
                    <label className='modal-field'>
                      상품 유형
                      <select value={productDraft.packageType} onChange={(event) => handlePackageTypeChange(event.target.value as PackageType)}>
                        <option value='드럼 전용'>드럼 전용</option>
                        <option value='인지 전용'>인지 전용</option>
                        <option value='드럼+인지 통합 패키지'>드럼+인지 통합 패키지</option>
                      </select>
                    </label>
                    <label className='modal-field'>
                      Google Play Product ID
                      <input value={productDraft.id} onChange={(event) => updateProductDraft('id', event.target.value)} />
                    </label>
                    <label className='modal-field'>
                      Base Plan ID
                      <input value={productDraft.base} onChange={(event) => updateProductDraft('base', event.target.value)} />
                    </label>
                    <label className='modal-field'>
                      Offer ID
                      <input value={productDraft.offer} onChange={(event) => updateProductDraft('offer', event.target.value)} />
                    </label>
                    <label className='modal-field'>
                      신규 판매
                      <select value={productDraft.sale} onChange={(event) => updateProductDraft('sale', event.target.value)}>
                        <option value='판매중'>판매중</option>
                        <option value='중지'>중지</option>
                      </select>
                    </label>
                  </div>
                  <div className='permission-checks'>
                    <label className='toggle'>
                      <input type='checkbox' checked={productDraft.drum} onChange={(event) => handleRightToggle('drum', event.target.checked)} />
                      드럼 연주 전용
                    </label>
                    <label className='toggle'>
                      <input type='checkbox' checked={productDraft.cognitive} onChange={(event) => handleRightToggle('cognitive', event.target.checked)} />
                      인지재활 전용
                    </label>
                  </div>
                </section>

                <div className='detail-footer'>
                  <button className='primary-button' type='button' onClick={handleSaveProduct}>상품 매핑 저장</button>
                </div>
              </div>
            ) : (
              <>
            <div className='tabbar' role='tablist'>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={`tab${activeTab === tab.id ? ' active' : ''}`}
                  type='button'
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'subsStatus' && (
              <div className='subview active'>
                <p className='implementation-note'>iOS 가입·구독은 화면 구성만 완료했으며, 실제 스토어 검증과 권한 연동은 추후 적용합니다.</p>
                <div className='toolbar'>
                  <div className='filter-group'>
                    <label className='inline-search'>
                      <Search size={15} />
                      <input
                        type='search'
                        placeholder='회원명 또는 상품명 검색'
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                      />
                    </label>
                    <div className='period-range'>
                      <select
                        className='filter period-filter'
                        defaultValue='month'
                        onChange={(event) => applyPeriodPreset(setSubscriptionPeriodStart, setSubscriptionPeriodEnd, event.target.value as PeriodMode)}
                      >
                        <option value='day'>일간</option>
                        <option value='week'>주간</option>
                        <option value='month'>월간</option>
                      </select>
                      <input className='date-filter' type='date' value={subscriptionPeriodStart} onChange={(event) => setSubscriptionPeriodStart(event.target.value)} />
                      <span>~</span>
                      <input className='date-filter' type='date' value={subscriptionPeriodEnd} onChange={(event) => setSubscriptionPeriodEnd(event.target.value)} />
                    </div>
                  </div>
                </div>
                <div className='table-wrap'>
                  <table>
                    <thead>
                      <tr>
                        <th>회원</th>
                        <th>스토어</th>
                        <th>상품</th>
                        <th>상품 유형</th>
                        <th>Product ID</th>
                        <th>Base Plan</th>
                        <th>상태</th>
                        <th>결제 완료일</th>
                        <th>다음 결제</th>
                        <th>잔여</th>
                        <th>자동 갱신</th>
                        <th>최근 확인</th>
                        <th>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubscriptions.length === 0 ? (
                        <tr><td colSpan={13}>검색 조건에 맞는 데이터가 없습니다.</td></tr>
                      ) : (
                        filteredSubscriptions.map((subscription) => (
                          <tr key={`${subscription.member}-${subscription.productId}`}>
                            <td>{subscription.member}</td>
                            <td><span className='badge primary'>{subscription.store}</span></td>
                            <td>{subscription.product}</td>
                            <td>{subscription.packageType}</td>
                            <td>{subscription.productId}</td>
                            <td>{subscription.base}</td>
                            <td><span className={`badge ${subscriptionStatusBadgeType(subscription.status)}`}>{subscription.status}</span></td>
                            <td>{subscription.paidAt}</td>
                            <td>{subscription.next}</td>
                            <td><span className={`badge ${subscription.dday === '-' ? '' : 'primary'}`}>{subscription.dday}</span></td>
                            <td>{subscription.renew}</td>
                            <td>{subscription.checked}</td>
                            <td>
                              <button className='secondary-button' type='button' onClick={() => showToast('구독 상세 페이지는 추후 제공됩니다.')}>상세</button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'payments' && (
              <div className='subview active'>
                <div className='toolbar'>
                  <div className='filter-group'>
                    <label className='inline-search'>
                      <Search size={15} />
                      <input
                        type='search'
                        placeholder='회원명, 주문번호 또는 상품명 검색'
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                      />
                    </label>
                    <div className='period-range'>
                      <select
                        className='filter period-filter'
                        defaultValue='month'
                        onChange={(event) => applyPeriodPreset(setPaymentPeriodStart, setPaymentPeriodEnd, event.target.value as PeriodMode)}
                      >
                        <option value='day'>일간</option>
                        <option value='week'>주간</option>
                        <option value='month'>월간</option>
                        <option value='year'>1년</option>
                      </select>
                      <input className='date-filter' type='date' value={paymentPeriodStart} onChange={(event) => setPaymentPeriodStart(event.target.value)} />
                      <span>~</span>
                      <input className='date-filter' type='date' value={paymentPeriodEnd} onChange={(event) => setPaymentPeriodEnd(event.target.value)} />
                    </div>
                  </div>
                </div>
                <div className='table-wrap'>
                  <table>
                    <thead>
                      <tr>
                        <th>주문번호</th>
                        <th>회원</th>
                        <th>상품명</th>
                        <th>금액</th>
                        <th>결제일</th>
                        <th>결제 유형</th>
                        <th>결제상태</th>
                        <th>승인 확인</th>
                        <th>환불</th>
                        <th>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.length === 0 ? (
                        <tr><td colSpan={10}>검색 조건에 맞는 데이터가 없습니다.</td></tr>
                      ) : (
                        filteredPayments.map((payment) => (
                          <tr key={payment.order}>
                            <td>{payment.order}</td>
                            <td>{payment.member}</td>
                            <td>{payment.product}</td>
                            <td>{payment.amount}</td>
                            <td>{payment.paidAt}</td>
                            <td>{payment.type}</td>
                            <td><span className={`badge ${paymentStatusBadgeType(payment.status)}`}>{payment.status}</span></td>
                            <td><span className={`badge ${ackBadgeType(payment.ack)}`}>{payment.ack}</span></td>
                            <td>{payment.refund}</td>
                            <td>
                              <button className='secondary-button' type='button' onClick={() => showToast('결제 원장 페이지는 추후 제공됩니다.')}>원장</button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div className='subview active'>
                <div className='settings-grid'>
                  {productList.map((product) => (
                    <article className='config-card' key={product.id}>
                      <PackageCheck size={20} />
                      <h3>{product.name}</h3>
                      <p>{product.id} · {product.base}</p>
                      <div className='permission-checks'>
                        <span className='badge primary'>{product.grade}</span>
                        <span className='badge success'>{product.rights}</span>
                        <span className={`badge ${product.sale === '판매중' ? 'success' : 'warning'}`}>{product.sale}</span>
                      </div>
                      <p>Offer ID: {product.offer}</p>
                      <button className='secondary-button' type='button' onClick={() => openProductEditor(product)}>설정 수정</button>
                    </article>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'errors' && (
              <div className='subview active'>
                <div className='issue-list'>
                  {paymentIssues.map((issue) => (
                    <div className='issue-item' key={issue.title}>
                      <div>
                        <strong>{issue.title}</strong>
                        <small>{issue.desc}</small>
                      </div>
                      <span className={`badge ${issue.type}`}>검토중</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
              </>
            )}
          </section>
        </main>
      </div>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default Subscriptions
